import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../data/siteTranslations';
import BookingForm from '../components/BookingForm';
import MapSection from '../components/MapSection';
import SEO from '../components/SEO';

export default function ContactPage() {
  const { lang } = useLanguage();
  const t = translations[lang].contactPage;
  const loc = translations[lang].location;

  return (
    <div className="space-y-0">
      <SEO
        title={lang === 'da' ? 'Kontakt & Tidsbestilling' : 'Contact & Booking'}
        description={lang === 'da'
          ? 'Kontakt Massage with Jette for tidsbestilling eller forespørgsler. Ring 81 98 37 07 eller send e-mail til MassagewithJette@gmail.com. Klinik i Salig på Østerbro.'
          : 'Contact Massage with Jette for appointments or inquiries. Call 81 98 37 07 or email MassagewithJette@gmail.com. Clinic located at Salig in Østerbro.'
        }
      />
      
      {/* Header */}
      <section className="py-16 bg-[#FAF7F2] border-b border-[#E8E0D7]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center max-w-3xl space-y-4">
          <span className="text-xs font-semibold uppercase tracking-widest text-[#2E3191] bg-[#EEF0F8] px-3.5 py-1 rounded-full border border-[#2E3191]/20 inline-block">
            {lang === 'da' ? 'Kontakt & Tidsbestilling' : 'Contact & Booking'}
          </span>
          <h1 className="font-serif-display text-4xl sm:text-5xl font-bold text-[#2C2825]">
            {t.title}
          </h1>
          <p className="text-base sm:text-lg text-[#6E6760] leading-relaxed">
            {t.subtitle}
          </p>
        </div>
      </section>

      {/* Main Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            
            {/* Contact Details Column */}
            <div className="lg:col-span-5 space-y-8">
              
              <div className="space-y-4">
                <h2 className="font-serif-display text-3xl font-bold text-[#2C2825]">
                  {lang === 'da' ? 'Direkte henvendelse' : 'Get in Touch'}
                </h2>
                <p className="text-sm text-[#6E6760] leading-relaxed">
                  {lang === 'da'
                    ? 'Du er altid velkommen til at ringe eller sende en e-mail, hvis du har spørgsmål til min behandlingstilgang eller ønsker at aftale en tid.'
                    : 'Feel free to call or send an email if you have questions about treatments or want to book an appointment.'}
                </p>
              </div>

              <div className="space-y-4 text-sm text-[#2C2825]">
                
                {/* Planway Online Booking Card */}
                <div className="p-6 rounded-2xl bg-[#EEF0F8] border border-[#2E3191]/30 flex flex-col gap-3 shadow-2xs">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-[#2E3191] text-white flex items-center justify-center text-xl shrink-0">
                      <i className="fa-solid fa-calendar-check" />
                    </div>
                    <div>
                      <span className="text-xs font-bold uppercase tracking-wider text-[#2E3191] block">
                        {lang === 'da' ? 'Online tidsbestilling' : 'Online Booking'}
                      </span>
                      <strong className="text-base text-[#2C2825] font-serif-display">
                        Planway Booking
                      </strong>
                    </div>
                  </div>
                  <p className="text-xs text-[#6E6760] leading-relaxed">
                    {lang === 'da'
                      ? 'Se alle ledige tider og book din tid direkte i kalenderen.'
                      : 'View available slots and schedule your appointment directly in the calendar.'}
                  </p>
                  <a
                    href="https://massage-with-jette.planway.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full py-3 px-4 rounded-xl bg-[#2E3191] hover:bg-[#1E2063] text-white text-xs sm:text-sm font-semibold flex items-center justify-center gap-2 transition-all shadow-xs"
                  >
                    <span>{lang === 'da' ? 'Åbn online booking kalender' : 'Open online booking calendar'}</span>
                    <i className="fa-solid fa-arrow-up-right-from-square text-xs" />
                  </a>
                </div>

                <div className="p-5 rounded-2xl bg-[#FAF7F2] border border-[#E8E0D7] flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#EEF0F8] text-[#2E3191] flex items-center justify-center text-xl shrink-0">
                    <i className="fa-solid fa-phone" />
                  </div>
                  <div>
                    <span className="text-xs text-[#6E6760] block">{loc.phoneTitle}</span>
                    <a
                      href="tel:81983707"
                      className="text-lg font-bold text-[#2C2825] hover:text-[#2E3191] transition-colors whitespace-nowrap phone-no-break"
                    >
                      81 98 37 07
                    </a>
                  </div>
                </div>

                <div className="p-5 rounded-2xl bg-[#FAF7F2] border border-[#E8E0D7] flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#EEF0F8] text-[#2E3191] flex items-center justify-center text-xl shrink-0">
                    <i className="fa-solid fa-envelope" />
                  </div>
                  <div>
                    <span className="text-xs text-[#6E6760] block">{loc.emailTitle}</span>
                    <a
                      href="mailto:MassagewithJette@gmail.com"
                      className="text-sm sm:text-base font-bold text-[#2C2825] hover:text-[#2E3191] transition-colors whitespace-nowrap email-no-break"
                    >
                      MassagewithJette@gmail.com
                    </a>
                  </div>
                </div>

                <div className="p-5 rounded-2xl bg-[#FAF7F2] border border-[#E8E0D7] flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#EEF0F8] text-[#2E3191] flex items-center justify-center text-xl shrink-0">
                    <i className="fa-solid fa-location-dot" />
                  </div>
                  <div>
                    <strong className="block text-base text-[#2C2825] font-serif-display">
                      {lang === 'da' ? 'Salig behandlerfællesskab' : 'Salig wellness space'}
                    </strong>
                    <p className="text-xs text-[#6E6760]">Masnedøgade 20, 2. sal</p>
                    <p className="text-xs text-[#6E6760]">2100 København Ø (Østerbro)</p>
                  </div>
                </div>

                <div className="p-5 rounded-2xl bg-[#EEF0F8] border border-[#2E3191]/30 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-white text-[#2E3191] flex items-center justify-center text-xl shrink-0">
                    <i className="fa-solid fa-car-side" />
                  </div>
                  <div>
                    <strong className="block text-xs font-bold text-[#2C2825] uppercase tracking-wider">{loc.outCallTitle}</strong>
                    <p className="text-xs text-[#2C2825]">{loc.outCallText}</p>
                  </div>
                </div>

              </div>

            </div>

            {/* Booking Form Column */}
            <div className="lg:col-span-7">
              <BookingForm />
            </div>

          </div>
        </div>
      </section>

      {/* Map Section */}
      <MapSection />

    </div>
  );
}
